import cn from "@/v2-ui/utils/utils.cn"
import { errorToast, toast } from "@/v2-ui/toast"
import Badge from "@/v2-backoffice-client/components/badge"
import {
  TooltipButton,
  type TooltipButtonProps,
} from "@/v2-backoffice-client/components/button"
import { useUserAccountEmailMutation } from "@/v2-backoffice-client/user/accountEmail/user.accountEmail.mutation"

export type UserAccountEmailManyButtonProps = TooltipButtonProps & {
  users: {
    userId: number | null | undefined
    password: string | null | undefined
  }[]
}

function UserAccountEmailManyButton(props: UserAccountEmailManyButtonProps) {
  const { users, className, ...buttonProps } = props

  const [sendAccountEmail, { loading, error }] = useUserAccountEmailMutation()
  const userCount = users.length

  errorToast(error, { title: "Failed to send account emails" })

  return (
    <TooltipButton
      className={cn(className, "relative")}
      iconSrc="paper-plane"
      content="Email each of these users their account details and starting password."
      disabled={userCount === 0 || loading}
      isLoading={loading}
      onClick={async () => {
        if (userCount === 0) return
        // errorPolicy "all": a failed send resolves with `errors` instead of
        // throwing (and errorToast above reports it), so count the ones that
        // actually went and say so — nothing else on screen changes.
        let sent = 0
        for (const user of users) {
          if (!user.userId || !user.password) continue
          const result = await sendAccountEmail({
            variables: {
              id: user.userId,
              password: user.password,
            },
          })
          if (result.data?.sendUserAccountEmail.Success) sent += 1
        }
        if (sent > 0) {
          toast({
            variant: "success",
            title: `Account email sent to ${sent} of ${userCount} user${userCount === 1 ? "" : "s"}`,
          })
        }
      }}
      {...buttonProps}
    >
      Send to All
      <Badge
        variant="neutral"
        size="tiny"
        className="absolute top-0 right-0 text-[8px] rounded-full"
      >
        {userCount}
      </Badge>
    </TooltipButton>
  )
}

export default UserAccountEmailManyButton
